import React, { useState, memo, useEffect } from 'react';
import { Button, Menu, MenuButton, MenuList, MenuDivider, MenuGroup, MenuItem } from '@chakra-ui/react';
import deepEqual from 'fast-deep-equal/es6/react';
import { CalendlyWidget } from 'shared-components';
import { ActionSelection } from '../../types';
import { getDropdownOptions } from './dropdown-options';
import { handleResendResetAction } from './resendResetPassword/handle-action';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { IStudentUserState } from '@/store/studentUsers/types';
import {
  resetPassword as actions_resetPassword,
  reinviteUser,
  resetStudentMfaRequest,
} from '@/store/studentUsers/actions';
import {
  studentAssignVisaSupport,
  studentUnassignVisaSupport,
  requestScheduledInterview as dashboardActions_requestScheduledInterview,
  cancelScheduledInterview as dashboardActions_cancelScheduledInterview,
  cancelRecordedInterview as dashboardActions_cancelRecordedInterview,
  onRequestRecordedInterview,
  cancelRecordedInterviewBulk as dashboardActions_cancelRecordedInterviewBulk,
  cancelScheduledInterviewBulk as dashboardActions_cancelScheduledInterviewBulk,
} from '@/store/dashboard/actions';
import { getUniEventTypesRecords } from '@/store/event_types/actions';
import { IUser, IRegionInterviewConfig, IStudentRow } from 'types';
import { areStudentsEqual } from '@/utilities/areStudentsEqual';
import { handleUpdateVisaSupportAction } from './assignVisaSupport';
import { EditServicesModal } from './editServices/EditServicesModal';
import { handleScheduledInterviewsAction } from './scheduledInterviews';
import { IEventType } from '@/store/event_types/types';
import RequestNewInterviewV2, { IRequestInterviewModalV2Data } from '@/components/pages/modals/requestNewInterviewV2';
import CancelInterviewV2, { ICancelInterviewModalV2Data } from '@/components/pages/modals/cancelInterviewV2';
import { EditImmigrationProvidersModal } from './editImmigrationProviders/EditImmigrationProvidersModal/EditImmigrationProvidersModal';
import { getAllSelected, isOnlyPathwayStudentsSelector } from '@/store/selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import AlertDialog, { IAlertDialogData } from '@/components/pages/modals/AlertDialog';
import { useTranslation } from "react-i18next";

export interface IDropdownOption {
  value: string;
  label: string;
}

export interface IGroup {
  // used when you need divider || label inside menu
  options: IDropdownOption[];
  label?: string;
}

export enum WarningTypeEnum {
  RESET_PASSWORD = 'RESET_PASSWORD',
  RESEND_INVITATION = 'RESEND_INVITATION',
  RESET_MFA = 'RESET_MFA',
}

// TODO add remaining actions
export const MoreActions = () => {
    const { t } = useTranslation('common');
  const manualVisaSupportEnabled = useSelector(({ config }: ApplicationState) => !!config.manualVisaSupportEnabled);
  const isPathwayProvider = useSelector(({ config }: ApplicationState) => !!config.isPathway);
  const isAddAndCancelInterviewVisible = useSelector(
    ({ config }: ApplicationState) => config.relatedPathwaySettings?.addAndCancelInterviewVisible ?? false,
  );
  const isCalendlyEnabled = useSelector(({ config }: ApplicationState) => !!config.CalendlyConfig?.isCalendlyEnabled);
  const isRecordedInterviewEnabled = useSelector(
    ({ config }: ApplicationState) => !!config?.recordedInterview?.enabled,
  );
  const selectedRows = useSelector(({ dashboard }: ApplicationState) => dashboard.selectedRows);
  const allStudents = useSelector(({ dashboard }: ApplicationState) => dashboard.students || [], areStudentsEqual);
  const userRole = useSelector(({ auth }: ApplicationState) => auth.role);
  const studentUsers: IStudentUserState = useSelector(({ studentUsers }: ApplicationState) => studentUsers);

  const [isCalendlyModal, setCalendlyModal] = useState(false);
  const [calendlyModalUrl, setCalendlyModalUrl] = useState<string>('');

  const hasPartners = useSelector(({ config }: ApplicationState) => !!config.partners && config.partners.length > 0);
  const hasImmigrationProviders = useSelector(
    ({ config }: ApplicationState) => !!config.immigrationProviders && config.immigrationProviders.length > 0,
  );

  const allStudentsSelected = useSelector(getAllSelected);

  const isOnlyPathwayStudents = useSelector(isOnlyPathwayStudentsSelector);

  const dispatch = useDispatch();

  const resetPassword = (uniId: string, username: string, pathway?: string) => {
    if (pathway) {
      dispatch(actions_resetPassword(uniId, username, pathway));
    } else {
      dispatch(actions_resetPassword(uniId, username));
    }
  };
  const resendInvite = (uniId: string, username: string, pathway?: string) => {
    if (pathway) {
      dispatch(reinviteUser(uniId, username, pathway));
    } else {
      dispatch(reinviteUser(uniId, username));
    }
  };

  const isExportUsersDisabled = !allStudents.length;

  const studentUserRecord =
    (selectedRows?.length ?? 0) === 1 && (studentUsers?.users?.length ?? 0) > 0
      ? (studentUsers.users.find((student: IUser) => {
          const [uni, studentNo] = (selectedRows[0] ?? '').split('#');
          return student.university === uni && student.studentNo === studentNo;
        }) ?? null)
      : null;

  const [isConfirmModal, setConfirmationModal] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState<IAlertDialogData>({
    modalTitle: '',
    descriptionText: '',
    confirmationButtonText: '',
    clickAction: () => {},
  });

  const [isServiceModalOpened, setIsServiceModalOpened] = useState(false);
  const [isImmigrationProviderModalOpened, setIsImmigrationProviderModalOpened] = useState(false);

  const onShowResendResetPasswordWarning = (
    warningType: WarningTypeEnum,
    studentUserRecord: IUser | null,
    { action }: { action: () => void },
  ) => {
    const studentCard = `${studentUserRecord?.givenName} ${studentUserRecord?.familyName} (${studentUserRecord?.studentNo})`;
    setConfirmationModal(true);
    setConfirmationModalData({
      modalTitle:
        warningType === WarningTypeEnum.RESET_PASSWORD
          ? 'Reset password?'
          : warningType === WarningTypeEnum.RESET_MFA
            ? 'Reset two-step verification?'
            : 'Resend invitation?',
      descriptionText:
        warningType === WarningTypeEnum.RESET_PASSWORD ? (
          <p>
            {t('common:areYouSureYouWantToResetThePassw')}
                              <br />
            <b>{studentCard}?</b>
          </p>
        ) : warningType === WarningTypeEnum.RESET_MFA ? (
          <p>
            {t('common:areYouSureYouWantToResetTwoStepV')}
                                  <br />
            <b>{studentCard}?</b>
          </p>
        ) : (
          <p>
            {t('common:areYouSureYouWantToSendAnotherIn')}
                                      <br />
            <b>{studentCard}?</b>
          </p>
        ),
      confirmationButtonText:
        warningType === WarningTypeEnum.RESET_PASSWORD
          ? 'Reset password'
          : warningType === WarningTypeEnum.RESET_MFA
            ? 'Reset'
            : 'Resend invitation',
      clickAction: action,
    });
  };

  const assignVisaSupport = (uni: string, studentNo: string, intakeId: string, offerId: string) => {
    dispatch(studentAssignVisaSupport(uni, studentNo, intakeId, offerId));
  };
  const unassignVisaSupport = (uni: string, studentNo: string, intakeId: string, offerId: string) => {
    dispatch(studentUnassignVisaSupport(uni, studentNo, intakeId, offerId));
  };

  const requestScheduledInterview = (uni: string, students: string[], eventType: IEventType) => {
    dispatch(dashboardActions_requestScheduledInterview(uni, students, eventType));
  };

  const cancelScheduledInterview = (
    uni: string,
    studentNo: string,
    intakeId: string,
    offerId: string,
    eventTypeId: string,
    eventKey: string,
    eventCancelUrl?: string,
  ) => {
    if (eventCancelUrl) {
      setCalendlyModal(true);
      setCalendlyModalUrl(eventCancelUrl);
    } else {
      dispatch(dashboardActions_cancelScheduledInterview(uni, studentNo, intakeId, offerId, eventTypeId, eventKey));
    }
  };

  const cancelRecordedInterview = (
    uni: string,
    studentNo: string,
    intakeId: string,
    offerId: string,
    interviewHash: string,
  ) => {
    dispatch(dashboardActions_cancelRecordedInterview(uni, studentNo, intakeId, offerId, interviewHash));
  };

  const requestRecordedInterview = (uni: string, students: string[], regionInterviewConfig: IRegionInterviewConfig) => {
    dispatch(onRequestRecordedInterview(uni, students, regionInterviewConfig));
  };

  const cancelRecordedInterviewBulk = (
    uni: string,
    students: string[],
    regionInterviewConfig: IRegionInterviewConfig,
  ) => {
    dispatch(dashboardActions_cancelRecordedInterviewBulk(uni, students, regionInterviewConfig));
  };

  const cancelScheduledInterviewBulk = (uni: string, students: string[], eventType: IEventType) => {
    dispatch(dashboardActions_cancelScheduledInterviewBulk(uni, students, eventType));
  };

  const [isRequestInterviewModal, setRequestInterviewModal] = useState(false);
  const [requestInterviewData, setRequestInterviewData] = useState<IRequestInterviewModalV2Data>({
    modalTitle: 'Invite to interview',
    descriptionText: 'Choose meeting type',
    confirmationButtonText: 'Send invitation',
    clickAction: () => {},
    uni: '',
    pathway: '',
  });

  const [isCancelInterviewModal, setCancelInterviewModal] = useState(false);
  const [cancelInterviewData, setCancelInterviewData] = useState<ICancelInterviewModalV2Data>({
    modalTitle: 'Cancel interview',
    noticeText:
      'If you are cancelling an interview that is not yet booked, the event will be removed, otherwise you will see a followup dialog to Cancel Event.',
    descriptionText: 'Choose interview to cancel',
    confirmationButtonText: 'Request cancellation',
    cancelButtonText: 'Don’t cancel',
    uni: '',
    pathway: '',
    clickAction: () => {},
    clickBulkAction: () => {},
    assignedInterviews: [],
  });

  const selectedStudents = allStudents?.filter(value => selectedRows.indexOf(value.p_key) > -1) ?? [];
  const areAllSelectedStudentsPathwaysEqual = selectedStudents.every(
    (student: IStudentRow) => student?.pathway === selectedStudents[0].pathway,
  );

  const onShowRequestInterviewActionModal = (modalData: IRequestInterviewModalV2Data) => {
    setRequestInterviewModal(true);
    setRequestInterviewData(modalData);
  };

  const onShowCancelInterviewActionModal = (modalData: ICancelInterviewModalV2Data) => {
    setCancelInterviewModal(true);
    setCancelInterviewData(modalData);
  };

  const onShowActionWarning = (modalData: IAlertDialogData) => {
    setConfirmationModal(true);
    setConfirmationModalData(modalData);
  };

  useEffect(() => {
    dispatch(getUniEventTypesRecords());
  }, [dispatch]);

  const onSelect = async action => {
    if ([ActionSelection.RESEND_INVITE, ActionSelection.RESET_PASSWORD].includes(action as ActionSelection)) {
      return await handleResendResetAction(
        action as ActionSelection,
        studentUserRecord,
        resendInvite,
        resetPassword,
        onShowResendResetPasswordWarning,
      );
    }
    if ([ActionSelection.RESET_MFA].includes(action as ActionSelection)) {
      onShowResendResetPasswordWarning(WarningTypeEnum.RESET_MFA, studentUserRecord, {
        action: () => !!studentUserRecord && dispatch(resetStudentMfaRequest(studentUserRecord)),
      });
    }

    if (
      [ActionSelection.ASSIGN_VISA_SUPPORT, ActionSelection.UNASSIGN_VISA_SUPPORT].includes(action as ActionSelection)
    ) {
      return await handleUpdateVisaSupportAction(
        action as ActionSelection,
        allStudents,
        selectedRows,
        assignVisaSupport,
        unassignVisaSupport,
        onShowActionWarning,
      );
    }

    if ([ActionSelection.SERVICES].includes(action as ActionSelection)) {
      setIsServiceModalOpened(true);
    }

    if ([ActionSelection.REQUEST_INTERVIEW, ActionSelection.CANCEL_INTERVIEW].includes(action as ActionSelection)) {
      return await handleScheduledInterviewsAction(
        action as ActionSelection,
        allStudents,
        selectedRows,
        requestRecordedInterview,
        requestScheduledInterview,
        cancelScheduledInterview,
        cancelRecordedInterview,
        cancelScheduledInterviewBulk,
        cancelRecordedInterviewBulk,
        onShowRequestInterviewActionModal,
        onShowCancelInterviewActionModal,
      );
    }

    if ([ActionSelection.IMMIGRATION_PROVIDERS].includes(action as ActionSelection)) {
      setIsImmigrationProviderModalOpened(true);
    }
  };

  const renderOptions = () => {
    const dropdownOptions: IDropdownOption[] | IGroup[] = getDropdownOptions(
      userRole,
      allStudents,
      selectedRows,
      studentUserRecord,
      manualVisaSupportEnabled,
      isCalendlyEnabled,
      isRecordedInterviewEnabled,
      isPathwayProvider,
      hasPartners,
      hasImmigrationProviders,
      isAddAndCancelInterviewVisible,
      areAllSelectedStudentsPathwaysEqual,
      allStudentsSelected,
      isOnlyPathwayStudents,
    ).filter(action => action.options.length !== 0);

    return dropdownOptions.map((option: IGroup, dropdownOptionIndex: number) => {
      if (option.label) {
        return (
          <React.Fragment key={`grouped-options-${dropdownOptionIndex}`}>
            <MenuDivider />

            <MenuGroup title={option.label}>
              {option.options.map((option: IDropdownOption, index: number) => (
                <MenuItem
                  data-value={option.value}
                  data-selector="select-option"
                  key={`grouped-menu-item-${dropdownOptionIndex}-${index}`}
                  value={option.value}
                  onClick={() => onSelect(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuGroup>
          </React.Fragment>
        );
      }
      return option.options.map((option: IDropdownOption, index: number) => (
        <MenuItem
          data-value={option.value}
          data-selector="select-option"
          key={`non-grouped-menu-item-${dropdownOptionIndex}-${index}`}
          value={option.value}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </MenuItem>
      ));
    });
  };

  return (
    <>
      {isConfirmModal && (
        <AlertDialog
          isOpen={true}
          modalTitle={confirmationModalData.modalTitle}
          descriptionText={confirmationModalData.descriptionText}
          confirmationButtonText={confirmationModalData.confirmationButtonText}
          id={'remove-tag'}
          onClickSuccess={() => {
            confirmationModalData.clickAction();
            setConfirmationModal(false);
          }}
          onClose={() => setConfirmationModal(false)}
        />
      )}
      {isServiceModalOpened && <EditServicesModal isOpen={isServiceModalOpened} setIsOpen={setIsServiceModalOpened} />}
      {isRequestInterviewModal && (
        <RequestNewInterviewV2
          modalTitle={requestInterviewData.modalTitle}
          descriptionText={requestInterviewData.descriptionText}
          confirmationButtonText={requestInterviewData.confirmationButtonText}
          uni={requestInterviewData.uni}
          pathway={requestInterviewData.pathway}
          selectedStudents={selectedStudents}
          onAddRequest={requestInterviewData.clickAction}
          onClose={() => setRequestInterviewModal(false)}
        />
      )}
      {isCancelInterviewModal && selectedRows.length !== 1 && (
        <RequestNewInterviewV2
          modalTitle={cancelInterviewData.modalTitle}
          descriptionText={cancelInterviewData.descriptionText}
          confirmationButtonText={cancelInterviewData.confirmationButtonText}
          uni={cancelInterviewData.uni}
          pathway={cancelInterviewData.pathway}
          selectedStudents={[]}
          isCancelling={true}
          onAddRequest={cancelInterviewData.clickBulkAction}
          onClose={() => setCancelInterviewModal(false)}
        />
      )}
      {isCancelInterviewModal && selectedRows.length === 1 && (
        <CancelInterviewV2
          modalTitle={cancelInterviewData.modalTitle}
          noticeText={cancelInterviewData.noticeText}
          descriptionText={cancelInterviewData.descriptionText}
          confirmationButtonText={cancelInterviewData.confirmationButtonText}
          cancelButtonText={cancelInterviewData.cancelButtonText}
          uni={requestInterviewData.uni}
          pathway={requestInterviewData.pathway}
          assignedInterviews={cancelInterviewData.assignedInterviews}
          onCancelRequest={cancelInterviewData.clickAction}
          onClose={() => setCancelInterviewModal(false)}
        />
      )}
      {isCalendlyModal && (
        <CalendlyWidget
          url={calendlyModalUrl}
          prefill={{ name: '', email: '', firstName: '', lastName: '' }}
          onClose={() => setCalendlyModal(false)}
        />
      )}
      {isImmigrationProviderModalOpened && (
        <EditImmigrationProvidersModal
          isOpen={isImmigrationProviderModalOpened}
          setIsOpen={setIsImmigrationProviderModalOpened}
        />
      )}
      <Menu>
        <MenuButton
          data-selector="action-input-field"
          as={Button}
          variant="ghost"
          size="sm"
          isDisabled={isExportUsersDisabled}
          rightIcon={<FontAwesomeIcon className="utility-icon" icon={regular('angle-down')} />}
        >
          {t('common:moreActions')}
                          </MenuButton>

        <MenuList data-selector="action-input-list" fontSize="sm">
          {renderOptions()}
        </MenuList>
      </Menu>
    </>
  );
};

export const MoreAction = memo(MoreActions, deepEqual);
